import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "../api/errors";
import { newIdempotencyKey, ordersApi, type OrderListQuery } from "../api/orders";
import type {
  OrderDetail,
  OrderHistory,
  OrderListPage,
  OrderStatus,
  OrderTransitionResult,
} from "../api/types";

/**
 * Reading orders, and moving one.
 *
 * Every query key starts with "orders", which is what makes the invalidation
 * after a transition a single statement rather than a list of keys somebody has
 * to keep in step with the screens. A new orders query added later is covered
 * by that automatically — the failure mode this guards against is the Orders
 * list still showing "placed" for an order the detail beside it has just
 * confirmed.
 */
export const orderKeys = {
  all: ["orders"] as const,
  list: (query: OrderListQuery) => ["orders", "list", query] as const,
  detail: (orderId: string) => ["orders", "detail", orderId] as const,
  history: (orderId: string) => ["orders", "history", orderId] as const,
};

/** How many orders one page of the table shows. */
export const ORDERS_PAGE_SIZE = 25;

export function useOrdersList(query: OrderListQuery) {
  return useQuery<OrderListPage>({
    queryKey: orderKeys.list(query),
    queryFn: ({ signal }) => ordersApi.list(query, signal),
    // The previous page stays on screen while the next one loads, so paging
    // and typing do not blank the table between answers.
    placeholderData: (previous) => previous,
  });
}

export function useOrder(orderId: string) {
  return useQuery<OrderDetail>({
    queryKey: orderKeys.detail(orderId),
    queryFn: ({ signal }) => ordersApi.detail(orderId, signal),
  });
}

export function useOrderHistory(orderId: string) {
  return useQuery<OrderHistory>({
    queryKey: orderKeys.history(orderId),
    queryFn: ({ signal }) => ordersApi.history(orderId, signal),
  });
}

/**
 * Move one order, then make every view of it agree again.
 *
 * NOTHING IS ASSUMED BEFORE THE SERVER ANSWERS. There is no optimistic update:
 * the status is not written into the cache when the button is pressed, only
 * when the backend has said what the order now is. An order is stock and money,
 * and a screen that shows "Cancelled" for the moment before a refusal arrives
 * is a screen an operator can act on twice.
 *
 * What IS written on success is the backend's own canonical order out of the
 * transition response — that is confirmed server state, not a guess — and then
 * everything under "orders" is invalidated so the list, its counts and the
 * history are refetched rather than patched.
 */
export function useOrderTransition(orderId: string) {
  const queryClient = useQueryClient();

  return useMutation<
    OrderTransitionResult,
    unknown,
    { target: OrderStatus; reason: string }
  >({
    mutationFn: ({ target, reason }) =>
      // One key per press. This client never retries a mutation by itself, so
      // a second key only ever exists because an administrator asked a second
      // time — which is a second intent, and the backend treats it as one.
      ordersApi.transition(orderId, target, reason, newIdempotencyKey()),

    onSuccess: async (result) => {
      queryClient.setQueryData(orderKeys.detail(orderId), result.order);
      // Everything: this order's detail and history, and every list query,
      // because a status change moves the order between filtered views and
      // changes the totals those views report.
      await queryClient.invalidateQueries({ queryKey: orderKeys.all });
    },

    onError: async (error) => {
      // A conflict means somebody else moved this order first, so what is on
      // screen is stale. Reload it and stop — the request is NEVER resent
      // automatically. Deciding what to do from the new state is the
      // operator's, and a silent retry would be this client deciding for them.
      if (error instanceof ApiError && error.kind === "conflict") {
        await queryClient.invalidateQueries({ queryKey: orderKeys.all });
      }
    },
  });
}
